/**
 * Estado de sesión del ERP.
 *
 * El SPA no guarda la sesión: la sesión vive en cookies que no puede leer
 * (ADR-009). Lo que guarda aquí es lo que necesita para **pintar**: quién
 * es el usuario y qué permisos tiene, para no ofrecer botones que el
 * servidor va a rechazar.
 *
 * La autorización real la decide el servidor, siempre. Esto es la
 * cortesía de no enseñar puertas cerradas.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { hasPermission as domainHasPermission, type Permission } from '@fibwifire/domain/browser';
import { ApiClient } from './api-client.js';
import { ApiError } from './api-error.js';

export interface SessionUser {
  userId: string;
  companyId: string;
  roles: string[];
  permissions: string[];
}

export interface LoginCredentials {
  email: string;
  password: string;
  companyTaxId?: string;
  totpCode?: string;
}

export type LoginOutcome =
  | { status: 'AUTHENTICATED' }
  /** El usuario tiene doble factor: hay que pedirle el código. */
  | { status: 'MFA_REQUIRED' };

interface SessionState {
  /** `undefined` mientras se comprueba si ya había sesión abierta. */
  user: SessionUser | null | undefined;
  login: (credentials: LoginCredentials) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
  api: ApiClient;
}

const SessionContext = createContext<SessionState | null>(null);

interface LoginResponse {
  status: 'AUTHENTICATED' | 'MFA_REQUIRED';
}

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);

  const api = useMemo(
    () => new ApiClient({ onSessionLost: () => setUser(null) }),
    [],
  );

  /**
   * ¿Había sesión abierta?
   *
   * Al cargar la página, las cookies pueden seguir vivas de ayer. No se
   * puede saber mirándolas —son `httpOnly`—, así que se pregunta. Si la
   * respuesta es 401, el cliente ya habrá intentado renovar por su cuenta.
   */
  useEffect(() => {
    let cancelled = false;

    api
      .get<SessionUser>('/web/session/me')
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<LoginOutcome> => {
      const result = await api.post<LoginResponse>('/web/session', credentials);

      if (result.status === 'MFA_REQUIRED') {
        return { status: 'MFA_REQUIRED' };
      }

      // El perfil se pide aparte y no se deriva de la respuesta del login:
      // los permisos efectivos los calcula el servidor a partir de los
      // roles, y ésa es la lista que debe mandar sobre lo que se pinta.
      setUser(await api.get<SessionUser>('/web/session/me'));
      return { status: 'AUTHENTICATED' };
    },
    [api],
  );

  const logout = useCallback(async () => {
    try {
      await api.delete('/web/session');
    } catch (error) {
      // Si el servidor no responde, la sesión local se cierra igual: para
      // quien está delante, «he cerrado sesión» no puede depender de que
      // la red funcione. El refresco caducará por su cuenta.
      if (!(error instanceof ApiError)) throw error;
    } finally {
      setUser(null);
    }
  }, [api]);

  const value = useMemo<SessionState>(
    () => ({ user, login, logout, api }),
    [user, login, logout, api],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession se ha usado fuera de <SessionProvider>');
  }
  return context;
}

/**
 * ¿Tiene el usuario este permiso?
 *
 * **Se usa la función del dominio, no una copia.** El servidor manda los
 * permisos sin expandir, con comodines —`assets.*` concede todas las
 * acciones del módulo, y `*` concede todo—, así que una reimplementación
 * «obvia» en el navegador escondería opciones que el usuario sí puede usar
 * o, peor, ofrecería las que no. Es exactamente el motivo de la ADR-001.
 */
export function hasPermission(user: SessionUser | null | undefined, permission: Permission): boolean {
  if (!user) return false;
  return domainHasPermission(user.permissions, permission);
}
